import React, { useEffect, useState } from 'react';
import {
  Outlet, NavLink, Link, useLocation, useNavigate,
} from 'react-router-dom';
import {
  Globe, User, LogOut, Menu, X, PanelLeftClose, PanelLeftOpen, Zap, Lock,
  ShieldAlert, ChevronDown, Sun, Moon,
} from 'lucide-react';
import client from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { applyAdminTheme } from '../lib/theme';
import { buildPermissionChecker } from './lib/permissions';
import {
  ADMIN_NAVIGATION, ROUTE_PERMISSIONS, ROUTE_PREFIX_PERMISSIONS,
  adminNavItemVisible, locateNavItem,
} from './lib/navigation';
import { applyAdminDesign, useAdminDesign } from './lib/adminDesign';
import { buildLicenseNotice } from './lib/licenseNotice';
import { preloadAdminGuide } from './lib/guide';
import {
  UPDATES_SEEN_EVENT, preloadAdminUpdates, unseenBadgeLabel, unseenUpdateCount,
} from './lib/updates';
import { adminLoginViaPortal } from '../lib/adminDomain';
import AdminAssistant from './components/AdminAssistant';
import AdminLogin from './AdminLogin';
import Logo from '../components/Logo';
import './admin.css';
import './admin-v2.css';

const AdminForbidden = ({ fallback }) => (
  <div className="admin-card admin-state" role="alert">
    <Lock size={28} className="text-amber-400" aria-hidden="true" />
    <h2>Bu sayfaya erişim yetkiniz yok</h2>
    <p>
      Rolünüz bu bölümü kapsamıyor. Erişim gerekiyorsa sistem sahibinden
      yetkilerinizin güncellenmesini isteyin.
    </p>
    {fallback && (
      <Link to={fallback} className="admin-btn-secondary">Yetkili olduğunuz sayfaya git</Link>
    )}
  </div>
);

// Dashboard'un finans akışıyla aynı aralık (Dashboard.FINANCE_REFRESH_MS).
const PENDING_BADGE_REFRESH_MS = 15000;

const navBadgeCount = (badge, { updates, pending }) => {
  if (badge === 'updates') return updates;
  if (badge === 'pending-deposit') return pending.deposit;
  if (badge === 'pending-withdraw') return pending.withdraw;
  return 0;
};

const AdminLayout = () => {
  const { user, logout, ready } = useAuth();
  const settings = useSettings();
  const {
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    site_theme: siteTheme,
  } = settings;
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Gece/gündüz modu yöneticinin tarayıcısındadır (bkz. lib/adminDesign.js).
  const design = useAdminDesign();
  // Elle açılan menü grupları. Kalıcı değildir: sayfa
  // yenilendiğinde ya da başka sayfaya geçildiğinde yalnızca açık sayfanın
  // grubu açık kalır — açılan grupların birikmesi menüyü yine uzun bir
  // listeye çeviriyordu.
  const [openGroups, setOpenGroups] = useState([]);
  // Daraltma durumu tek gerçek kaynaktır: CSS artık hiçbir genişlikte sidebar'ı
  // zorla daraltmaz. Yönetici daha önce seçim yapmadıysa dar masaüstü
  // ekranlarında (<1200px) daraltılmış açılır; seçim yapıldıysa o korunur.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('admin_sidebar_collapsed');
    if (stored === 'true' || stored === 'false') return stored === 'true';
    return typeof window !== 'undefined' && window.innerWidth < 1200;
  });

  // Panel ayrı bir alan adındaysa portalın giriş penceresi burada yoktur:
  // yönlendirme sonsuz döngü olurdu (kök adres panele, panel köke). Bakım
  // modunda da yoktur — ana sayfa yalnızca duyuruyu çizer. İki durumda da
  // panelin kendi giriş ekranı çizilir (bkz. lib/adminDomain.js).
  const viaPortal = adminLoginViaPortal(settings);

  useEffect(() => {
    if (viaPortal && ready && (!user || !user.is_admin)) {
      navigate('/', {
        replace: true,
        state: user ? { adminDenied: true } : { auth: 'login' },
      });
    }
  }, [viaPortal, ready, user, navigate]);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Kılavuz 167 KB'lık yerel bir operasyon belgesidir. Yönetici paneli
  // açılır açılmaz arka planda alınır; menü tıklaması ağ isteğini beklemeden
  // oturum belleğindeki kopyayı yeni sekmede gösterir.
  useEffect(() => {
    if (ready && user?.is_admin) preloadAdminGuide();
  }, [ready, user]);

  // Yenilikler rozeti: menüdeki rakam son okumadan sonra eklenen madde
  // sayısıdır. Liste panel açılırken bir kez alınır (sayfanın kendisi aynı
  // önbelleği okur, ikinci istek atılmaz) ve sayfa listeyi okundu
  // işaretlediğinde olayla sıfırlanır — sayfadan çıkıp girmek gerekmez.
  const [updatesBadge, setUpdatesBadge] = useState(0);

  useEffect(() => {
    if (!ready || !user?.is_admin) return undefined;
    let cancelled = false;
    const refresh = () => preloadAdminUpdates().then((data) => {
      if (!cancelled) setUpdatesBadge(unseenUpdateCount(data.entries));
    });
    refresh();
    window.addEventListener(UPDATES_SEEN_EVENT, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(UPDATES_SEEN_EVENT, refresh);
    };
  }, [ready, user]);

  // Talep sayfalarının rozeti: bekleyen yatırım ve çekim
  // sayısı. Dashboard'un hafif ucu okunur ve aynı aralıkla, yalnızca sekme
  // görünürken tazelenir; ağır finans listesi bu tur için çağrılmaz.
  const [pendingBadges, setPendingBadges] = useState({ deposit: 0, withdraw: 0 });
  const pendingBadgeOn = ready && Boolean(user?.is_admin)
    && buildPermissionChecker(user?.admin_permissions)('finance.read')
    && buildPermissionChecker(user?.admin_permissions)('dashboard.read');

  useEffect(() => {
    if (!pendingBadgeOn) return undefined;
    let cancelled = false;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data } = await client.get('/admin/finance-notifications');
        if (!cancelled) {
          setPendingBadges({
            deposit: Number(data?.pending_deposit_count || 0),
            withdraw: Number(data?.pending_withdraw_count || 0),
          });
        }
      } catch {
        // Rozet okunamazsa eski sayı kalır; menü çalışmaya devam eder.
      }
    };
    refresh();
    const timer = setInterval(refresh, PENDING_BADGE_REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [pendingBadgeOn]);

  useEffect(() => {
    localStorage.setItem('admin_sidebar_collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    applyAdminDesign({ design: design.design, palette: design.palette, mode: design.mode });
  }, [design.design, design.palette, design.mode]);
  // Panelden çıkınca (oyuncu portalına dönüş) kökteki işaretler kalmasın.
  useEffect(() => () => applyAdminDesign(null), []);

  useEffect(() => setOpenGroups([]), [location.pathname]);

  const toggleGroup = (id) => setOpenGroups((current) => (
    current.includes(id)
      ? current.filter((entry) => entry !== id)
      : [...current, id]
  ));

  // Lisans kısıtı bütün paneli ilgilendirir: kısıt etkinken oyun açma, bahis
  // ve para yatırma durur. Bandı o an panelde olan her yönetici görmelidir,
  // yalnızca lisansı yönetebilen değil.
  const [licenseBanner, setLicenseBanner] = useState(null);

  useEffect(() => {
    if (!ready || !user?.is_admin) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await client.get('/admin/license/banner');
        if (!cancelled) setLicenseBanner(data);
      } catch {
        // Durum alınamazsa band çizilmez; panel çalışmaya devam eder.
        if (!cancelled) setLicenseBanner(null);
      }
    };
    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [ready, user]);

  // Alan adı değişikliği: eski domainde kalan callback/dönüş adresleri
  // sunucuda kendiliğinden taşınır (services/domain_autofix.py). Panel
  // açılışta yalnızca bu taşımayı tetikler; bant ya da uyarı çizilmez.
  useEffect(() => {
    if (!ready || !user?.is_admin) return undefined;
    client.get('/admin/domain/check').catch(() => {});
    return undefined;
  }, [ready, user?.is_admin]);

  // Panelin vurgu renkleri site teması ile aynı kaynaktan gelir; Genel
  // Ayarlar'da renk kaydedilince ayar yenilenir ve panel de yeni renge geçer.
  useEffect(() => {
    applyAdminTheme(primaryColor, secondaryColor);
  }, [primaryColor, secondaryColor]);

  const signOut = () => {
    logout();
    navigate('/');
  };

  if (!ready) {
    return (
      <div className="admin-body flex items-center justify-center">
        <div className="text-slate-400">Yükleniyor...</div>
      </div>
    );
  }
  if (!user || !user.is_admin) {
    // Portal varsa yukarıdaki yönlendirme devreye girer; panel adresinde
    // giriş buradan yapılır.
    return viaPortal ? null : <AdminLogin />;
  }

  // İzin listesi gelmezse menü boş kalır; eksik veriyi "her şey serbest"
  // saymak, backend'in reddedeceği ekranları açık gösterirdi.
  const can = buildPermissionChecker(user.admin_permissions);

  const currentPath = location.pathname.replace(/\/+$/, '') || '/admin';
  const requiredPermission = ROUTE_PERMISSIONS[currentPath]
    || ROUTE_PREFIX_PERMISSIONS.find(
      ([prefix]) => currentPath.startsWith(prefix))?.[1];
  const routeAllowed = !requiredPermission || can(requiredPermission);
  const licenseNotice = buildLicenseNotice(licenseBanner);
  const fallbackPath = Object.keys(ROUTE_PERMISSIONS).find(
    (path) => path !== currentPath && can(ROUTE_PERMISSIONS[path]),
  );

  const located = locateNavItem(currentPath);

  const renderNavItem = (item) => (item.action ? (
    <button
      key={item.key}
      type="button"
      title={sidebarCollapsed ? item.label : undefined}
      className="admin-sidebar-link w-full"
      onClick={() => {
        setDrawerOpen(false);
        item.action();
      }}
    >
      <item.icon size={16} />
      <span>{item.label}</span>
    </button>
  ) : (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      title={sidebarCollapsed ? item.label : undefined}
      className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : ''}`}
    >
      <item.icon size={16} />
      <span>{item.label}</span>
      {item.badge && (() => {
        const count = navBadgeCount(item.badge, {
          updates: updatesBadge, pending: pendingBadges,
        });
        if (count <= 0) return null;
        return (
          <span
            className="admin-nav-badge"
            title={item.badge === 'updates'
              ? `${count} yeni madde`
              : `${count} bekleyen talep`}
          >
            {unseenBadgeLabel(count)}
          </span>
        );
      })()}
    </NavLink>
  ));

  return (
    <div className={`admin-body ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {drawerOpen && (
        <button
          type="button"
          className="admin-sidebar-backdrop"
          aria-label="Menüyü kapat"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside className={`admin-sidebar ${drawerOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-brand">
          <div className="admin-brand-mark"><Zap size={18} fill="currentColor" /></div>
          <div className="min-w-0">
            <div className="max-w-[190px] overflow-hidden">
              <Logo size="sm" linked={false} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Yönetim Merkezi</div>
          </div>
          <button
            type="button"
            className="icon-btn view ml-auto md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-label="Menüyü kapat"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="admin-sidebar-nav" aria-label="Yönetim menüsü">
          {ADMIN_NAVIGATION.map((group) => {
            const visibleItems = group.items.filter(
              (item) => adminNavItemVisible(item, can, siteTheme),
            );
            if (!visibleItems.length) return null;
            // Açık sayfanın grubu her zaman açıktır; daraltılmış menüde
            // başlık görünmediği için bütün maddeler çizilir.
            const isCurrent = located?.group.id === group.id;
            const isOpen = sidebarCollapsed || isCurrent || openGroups.includes(group.id);
            const groupBadge = visibleItems.reduce((sum, item) => sum + (item.badge
              ? navBadgeCount(item.badge, { updates: updatesBadge, pending: pendingBadges })
              : 0), 0);
            return (
              <section
                key={group.id}
                className={`admin-nav-group v2-nav-group ${isOpen ? 'is-open' : ''} ${isCurrent ? 'is-current' : ''}`}
              >
                <button
                  type="button"
                  className="v2-nav-toggle"
                  aria-expanded={isOpen}
                  aria-controls={`v2-nav-${group.id}`}
                  onClick={() => toggleGroup(group.id)}
                  disabled={isCurrent && !sidebarCollapsed}
                  title={isCurrent ? 'Açık sayfanın grubu' : undefined}
                >
                  <group.icon size={16} aria-hidden="true" />
                  <span>{group.label}</span>
                  {!isOpen && groupBadge > 0 && (
                    <span className="admin-nav-badge">{unseenBadgeLabel(groupBadge)}</span>
                  )}
                  <ChevronDown size={14} className="v2-nav-chevron" aria-hidden="true" />
                </button>
                <div id={`v2-nav-${group.id}`} className="v2-nav-items" hidden={!isOpen}>
                  {visibleItems.map(renderNavItem)}
                </div>
              </section>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          {/* Panel ayrı bir alan adındayken göreli bir adres panelin kendi
              köküne, oradan da panele geri dönerdi. Adres sunucudan gelir ve
              site domaini değiştiğinde (104 → 105) kendiliğinden güncellenir. */}
          <a
            href={settings.site_base || '/'}
            target="_blank"
            rel="noreferrer"
            className="admin-sidebar-link"
          >
            <Globe size={16} />
            <span>Siteyi Görüntüle</span>
          </a>
          <button type="button" onClick={signOut} className="admin-sidebar-link danger w-full">
            <LogOut size={16} />
            <span>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      <div className="admin-shell">
        <header className="admin-header sticky top-0 z-30">
          <div className="h-16 px-4 md:px-7 flex items-center gap-3">
            <button
              type="button"
              className="icon-btn view md:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Yönetim menüsünü aç"
            >
              <Menu size={18} />
            </button>
            <button
              type="button"
              className="icon-btn view hidden md:inline-flex"
              onClick={() => setSidebarCollapsed((value) => !value)}
              aria-label={sidebarCollapsed ? 'Kenar menüsünü genişlet' : 'Kenar menüsünü daralt'}
              aria-expanded={!sidebarCollapsed}
              title={sidebarCollapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            <nav className="v2-crumb min-w-0" aria-label="Konum">
              <span>{located ? located.group.label : 'Yönetim Paneli'}</span>
              {located && (
                <>
                  <span aria-hidden="true">/</span>
                  <strong>{located.item.label}</strong>
                </>
              )}
            </nav>
            <div className="ml-auto flex items-center gap-2">
              <div className="v2-theme-controls" role="group" aria-label="Panel teması">
                <button
                  type="button"
                  className="icon-btn view"
                  onClick={() => design.update({ mode: design.mode === 'dark' ? 'light' : 'dark' })}
                  aria-label={design.mode === 'dark' ? 'Gündüz moduna geç' : 'Gece moduna geç'}
                  title={design.mode === 'dark' ? 'Gündüz modu' : 'Gece modu'}
                >
                  {design.mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                </button>
              </div>
              <div className="flex items-center gap-2 px-3 h-10 rounded-xl admin-user-chip">
                <div className="admin-user-avatar"><User size={13} /></div>
                <span className="text-sm font-semibold max-w-28 truncate">{user.username}</span>
              </div>
            </div>
          </div>
        </header>
        <main className="admin-main">
          {licenseNotice && (
            <div
              className={`admin-license-banner ${licenseNotice.blocked ? 'is-blocked' : ''}`}
              role="alert"
            >
              <ShieldAlert size={18} aria-hidden="true" />
              <div>
                <strong>{licenseNotice.title}</strong>
                <span>{licenseNotice.body}</span>
              </div>
              {licenseBanner.can_manage && (
                <Link to="/admin/license" className="admin-btn admin-btn-secondary">
                  Lisansı aç
                </Link>
              )}
            </div>
          )}
          {routeAllowed ? <Outlet /> : <AdminForbidden fallback={fallbackPath} />}
        </main>
      </div>
      {/* Yardım penceresi yalnızca panel kabuğunda çizilir; oyuncu portalına
          taşınmaz (asistan operasyon bilgisi konuşur). */}
      {!can.affiliateOnly && <AdminAssistant />}
    </div>
  );
};

export default AdminLayout;
